import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Grid, Button, CircularProgress } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { WorkSheet, utils } from "xlsx";
import FormSection from "./FormSection";
import AlandDataImport from "./AlandDataImport";
import ExcelImportPreview from "./ExcelImportPreview";
import { ExcelRow } from "../../types/multiMaterialFormTypes";
import { useSendAlandDataMutation } from "../../app/services/user";
import SuccessSnackbar from "../SuccessSnackbar";

// Simplified form state for Åland import
interface AlandFormState {
  rows: ExcelRow[];
  files: File[];
}

const AlandImport = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const [formState, setFormState] = useState<AlandFormState>({
    rows: [],
    files: [],
  });

  const [sendAlandData, { isLoading: isSending }] = useSendAlandDataMutation();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [importSuccessful, setImportSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const updateFormState = (newState: Partial<AlandFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  const onImport = (ws: WorkSheet, files: File[]) => {
    updateFormState({ rows: utils.sheet_to_json<ExcelRow>(ws), files });
  };

  const saveData = async () => {
    if (!formState.rows.length) {
      setErrorMessage(t('Ei tietoja tallennettavaksi'));
      setImportSuccessful(false);
      setShowSnackbar(true);
      return;
    }

    try {
      // Simplified FormData structure for Åland
      const formData = new FormData();
      formData.append('rows', JSON.stringify(formState.rows));
      formData.append('material', 'ALAND'); // Fixed material type for Åland
      formData.append('isAccepted', 'true'); // Default to accepted
      formData.append('isIncoming', 'true'); // Default to incoming
      
      formState.files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await sendAlandData(formData);
      
      if ((response as any).error) {
        setErrorMessage((response as any).error.data?.message || t('Virhe tietojen tallennuksessa'));
        setImportSuccessful(false);
      } else {
        setImportSuccessful(true);
        setErrorMessage('');
        // Reset form after successful submission
        setFormState({
          rows: [],
          files: [],
        });
        window.scrollTo(0, 0);
      }
    } catch (error: any) {
      setErrorMessage(error?.data?.message || t('Virhe tietojen tallennuksessa'));
      setImportSuccessful(false);
    }
    
    setShowSnackbar(true);
  };

  return (
    <>
      <SuccessSnackbar
        open={showSnackbar}
        setOpen={setShowSnackbar}
        successful={importSuccessful}
        successMessage={t('Tiedot tallennettu onnistuneesti')}
        errorMessage={`${t('Virhe')}: ${errorMessage}`}
      />

      {/* File Upload - Simplified, no material/type selection needed */}
      <FormSection title={t('alandImport.title.addFile')}>
        <Grid item xs={6} mt={3}>
          <AlandDataImport onImport={onImport} files={formState.files} />
        </Grid>
      </FormSection>

      {/* Preview */}
      <FormSection
        title={t('excelImport.title.preview')}
        show={Boolean(formState.rows.length)}
      >
        <ExcelImportPreview rows={formState.rows} />
      </FormSection>

      {/* Submit */}
      <FormSection show={Boolean(formState.rows.length)}>
        <Grid container justifyContent="center">
          <Button
            onClick={saveData}
            variant="contained"
            disabled={!formState.rows.length}
            sx={{ minWidth: '12em', justifySelf: 'center', height: theme.spacing(6) }}
          >
            {isSending && (
              <CircularProgress sx={{ mr: 2, svg: { color: 'white!important' } }} size={15} />
            )}
            {t('actions.submit')}
          </Button>
        </Grid>
      </FormSection>
    </>
  );
};

export default AlandImport;
